import { SearchOutlined } from '@ant-design/icons';
import { Button, Divider, Form, Input, Select, Space, Table, Tooltip, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

import InputText from '~/components/InputText';
import api from '~/services/api';
import history from '~/services/history';

import InventorySummaryByLocation from '../Inventory/InventorySummaryByLocation';
import { Container, TitleContainer } from './styles';

export default function InventoryStorages() {
  const [form] = Form.useForm();
  const [inventoryList, setInventoryList] = useState([]);
  const [searchTxt, setSearchTxt] = useState('');
  const [locationFilters, setLocationFilters] = useState(null);
  const [checkedLocations, setCheckedLocations] = useState(null);
  const [locationKey, setLocationKey] = useState(0);

  const { Title, Paragraph } = Typography;

  const handleSearch = async (values = {}, clearLocations = false) => {
    const searchQuery = values.q?.trim() || '';
    const capacities = values.Capacity || [];
    const types = values.Type || [];
    const formFactors = values.FormFactor || [];
    const controllers = values.Controller || [];
    const conditions = values.Condition || [];

    setSearchTxt(searchQuery);

    const params = new URLSearchParams();
    if (searchQuery) {
      params.append('searchQuery', encodeURIComponent(searchQuery));
    }

    if (capacities.length > 0) {
      params.append('capacity', capacities.join(','));
    }

    if (types.length > 0) {
      params.append('type', types.join(','));
    }

    if (formFactors.length > 0) {
      params.append('formFactor', formFactors.join(','));
    }

    if (controllers.length > 0) {
      params.append('controller', controllers.join(','));
    }

    if (conditions.length > 0) {
      params.append('condition', conditions.join(','));
    }

    if (!clearLocations && checkedLocations) {
      params.append('locationFilters', checkedLocations);
    }

    history.replace(`inventoryStorages?${params}`);

    const inventoryListData = await api.get(`inventory_storages?${params}`);
    if (!locationFilters) {
      setLocationFilters(() => Object.keys(inventoryListData.data.inventoryByLocation).filter((el) => el !== 'Total'));
    }
    setInventoryList(() => inventoryListData.data);
  };

  useEffect(() => {
    handleSearch();
  }, []); //eslint-disable-line

  const getStorageInGb = (capacity, unit) => {
    const numericCapacity = Number.parseFloat(String(capacity ?? '').replace(',', '.'));
    if (Number.isNaN(numericCapacity)) {
      return Number.POSITIVE_INFINITY;
    }

    const normalizedUnit = String(unit ?? 'GB')
      .trim()
      .toUpperCase();
    const multipliers = {
      MB: 1 / 1024,
      GB: 1,
      TB: 1024,
      PB: 1024 * 1024,
    };

    return numericCapacity * (multipliers[normalizedUnit] ?? 1);
  };

  const getCapacityLabel = (capacity, unit) => {
    const capacityText = String(capacity ?? '').trim();
    const unitText = String(unit ?? '').trim();
    return [capacityText, unitText].filter(Boolean).join(' ');
  };

  const columns = [
    {
      title: 'Brand',
      dataIndex: 'brand',
      sorter: (a, b) => a.brand.trim().localeCompare(b.brand.trim()),
    },
    {
      title: 'Part Number',
      dataIndex: 'partNumber',
      sorter: (a, b) => a.partNumber.trim().localeCompare(b.partNumber.trim()),
      render: (text, row) => (
        <Link
          to={() => {
            if (searchTxt === null || searchTxt === undefined) {
              return `/inventoryStorages/${row.id}`;
            }
            return `/inventoryStorages/${row.id}?searchQuery=${searchTxt}`;
          }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Paragraph style={{ color: '#1890ff', whiteSpace: 'nowrap', margin: 0 }} copyable>
            {text}
          </Paragraph>
        </Link>
      ),
    },
    {
      title: 'Capacity',
      dataIndex: 'capacity',
      sorter: (a, b) => {
        const storageDiff = getStorageInGb(a.capacity, a.unit) - getStorageInGb(b.capacity, b.unit);
        if (storageDiff !== 0) {
          return storageDiff;
        }

        return getCapacityLabel(a.capacity, a.unit).localeCompare(getCapacityLabel(b.capacity, b.unit));
      },
      render: (_, row) => getCapacityLabel(row.capacity, row.unit),
      width: 140,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 80,
    },
    {
      title: 'Form Factor',
      dataIndex: 'formFactor',
      width: 120,
    },
    {
      title: 'Controller',
      dataIndex: 'controller',
      sorter: (a, b) => (a.controller || '').localeCompare(b.controller || ''),
      width: 100,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      sorter: (a, b) => (a.description || '').localeCompare(b.description || ''),
      render: (text) => (
        <Tooltip
          title={
            <Paragraph copyable={{ text }} style={{ color: 'white', margin: 0 }}>
              {text}
            </Paragraph>
          }
          overlayInnerStyle={{ maxWidth: '500px' }}
        >
          <Paragraph
            style={{
              maxWidth: '450px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              margin: 0,
              cursor: 'pointer',
            }}
          >
            {text}
          </Paragraph>
        </Tooltip>
      ),
      width: 600,
    },
    {
      title: 'Quantity',
      dataIndex: 'total',
      sorter: (a, b) => a.total - b.total,
      render: (text) => (text === 0 ? '' : text),
      align: 'center',
    },
  ];

  const handleLocationFilterChange = useCallback(
    async (param) => {
      const formValues = form.getFieldsValue();
      const searchQuery = formValues.q?.trim() || '';
      const capacities = formValues.Capacity || [];
      const types = formValues.Type || [];
      const conditions = formValues.Condition || [];
      const formFactors = formValues.FormFactor || [];
      const controllers = formValues.Controller || [];

      const params = new URLSearchParams();
      if (searchQuery) {
        params.append('searchQuery', encodeURIComponent(searchQuery));
      }

      if (capacities.length > 0) {
        params.append('capacity', capacities.join(','));
      }

      if (types.length > 0) {
        params.append('type', types.join(','));
      }

      if (formFactors.length > 0) {
        params.append('formFactor', formFactors.join(', '));
      }

      if (controllers.length > 0) {
        params.append('controller', controllers.join(', '));
      }

      if (conditions.length > 0) {
        params.append('condition', conditions.join(','));
      }

      params.append('locationFilters', param);

      const response = await api.get(`inventory_storages?${params}`);

      setInventoryList(() => response.data);
    },
    [form]
  );

  function downloadExcel(data) {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `InventoryStoragesReport.xlsx`);
  }

  const getDetailedReportFilename = (contentDisposition) => {
    const fallbackName = 'inventory-storage-detailed-report.xlsx';
    if (!contentDisposition) return fallbackName;

    const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FileNameMatch?.[1]) {
      return decodeURIComponent(utf8FileNameMatch[1]);
    }

    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }

    return fallbackName;
  };

  const handleDownloadDetailedReport = async () => {
    try {
      const formValues = form.getFieldsValue();
      const searchQuery = formValues.q?.trim() || '';
      const capacities = formValues.Capacity || [];
      const types = formValues.Type || [];
      const formFactors = formValues.FormFactor || [];
      const controllers = formValues.Controller || [];
      const conditions = formValues.Condition || [];

      const params = new URLSearchParams();
      if (searchQuery) {
        params.append('searchQuery', encodeURIComponent(searchQuery));
      }

      if (capacities.length > 0) {
        params.append('capacity', capacities.join(','));
      }

      if (types.length > 0) {
        params.append('type', types.join(','));
      }

      if (formFactors.length > 0) {
        params.append('formFactor', formFactors.join(','));
      }

      if (controllers.length > 0) {
        params.append('controller', controllers.join(','));
      }

      if (conditions.length > 0) {
        params.append('condition', conditions.join(','));
      }

      if (checkedLocations) {
        params.append('locationFilters', checkedLocations);
      }

      const response = await api.get(`inventory-storages/detailed/export?${params.toString()}`, {
        responseType: 'blob',
      });

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', getDetailedReportFilename(response.headers['content-disposition']));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      toast.error('Failed to download detailed report.');
    }
  };

  return (
    <Container>
      <Space direction="vertical">
        <TitleContainer>
          <Title level={3}>Storage Inventory</Title>
        </TitleContainer>
        <Space size="small">
          <Form
            form={form}
            onFinish={handleSearch}
            layout="inline"
            initialValues={{
              q: '',
              Capacity: [],
              Type: [],
              Condition: [],
              FormFactor: [],
              Controller: [],
            }}
          >
            <Space.Compact block style={{ marginLeft: 8 }}>
              <Input.Group compact style={{ display: 'inline-block' }}>
                <Form.Item name="q" style={{ marginBottom: 0, marginRight: 0, display: 'inline-block' }}>
                  <InputText placeholder="Enter P/N or description" onPressEnter={() => form.submit()} style={{ borderRight: 0 }} />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, display: 'inline-block' }}>
                  <Button type="button" onClick={() => form.submit()} icon={<SearchOutlined />} />
                </Form.Item>
              </Input.Group>

              <Form.Item name="Capacity" style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder="Capacity"
                  style={{ width: 140 }}
                  options={inventoryList?.filterOptions?.capacity.map((c) => ({ label: c, value: c }))}
                  allowClear
                />
              </Form.Item>
              <Form.Item name="Type" style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder="Type"
                  style={{ width: 140 }}
                  options={inventoryList?.filterOptions?.type.map((t) => ({ label: t, value: t }))}
                  allowClear
                />
              </Form.Item>

              <Form.Item name="FormFactor" style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder="Form Factor"
                  style={{ width: 140 }}
                  options={inventoryList?.filterOptions?.formFactor.map((f) => ({ label: f, value: f }))}
                  allowClear
                />
              </Form.Item>

              <Form.Item name="Controller" style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder="Controller"
                  style={{ width: 140 }}
                  options={inventoryList?.filterOptions?.controller.map((c) => ({ label: c, value: c }))}
                  allowClear
                />
              </Form.Item>

              <Form.Item name="Condition" style={{ marginBottom: 0 }}>
                <Select
                  mode="multiple"
                  placeholder="Condition"
                  style={{ width: 160 }}
                  options={inventoryList?.filterOptions?.condition.map((c) => ({ label: c, value: c }))}
                  allowClear
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button type="default" htmlType="submit">
                  Apply Filters
                </Button>
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setCheckedLocations(null);
                    setLocationKey((k) => k + 1);
                    handleSearch({}, true);
                  }}
                >
                  Clean Filters
                </Button>
              </Form.Item>
            </Space.Compact>
          </Form>

          {inventoryList.inventoryByLocation && Object.keys(inventoryList.inventoryByLocation).length > 0 && (
            <InventorySummaryByLocation
              key={locationKey}
              data={inventoryList.inventoryByLocation}
              setLocationFilters={setLocationFilters}
              setCheckedLocations={setCheckedLocations}
              checkedLocations={checkedLocations}
              handleSearch={handleSearch}
              handleLocationFilterChange={handleLocationFilterChange}
            />
          )}
        </Space>
      </Space>
      <Divider />
      <Space>
        <Button type="primary" className="ant-dropdown-link" style={{ width: 200 }} onClick={() => downloadExcel(inventoryList.inventoryCount)}>
          Download summary report
        </Button>

        <Button type="primary" className="ant-dropdown-link" style={{ width: 200 }} onClick={handleDownloadDetailedReport}>
          Download detailed report
        </Button>
      </Space>
      <Table
        style={{ marginTop: '2rem' }}
        columns={columns}
        pagination={false}
        dataSource={inventoryList.inventoryCount}
        scroll={{ x: 1500, y: 'calc(100vh - 28em)' }}
        rowKey="id"
        size="small"
      />
    </Container>
  );
}
